import { v4 as uuidv4 } from 'uuid';
import { ValidationError } from '../utils/errors.js';
import { validateTimeSlot } from '../utils/validators.js';

/**
 * Represents available booking time slots
 */
export class TimeSlot {
  /**
   * Create a new TimeSlot
   * @param {Object} data - TimeSlot configuration
   * @param {string} [data._id] - Unique identifier (auto-generated if not provided)
   * @param {Date} data.start - Slot start time
   * @param {Date} data.end - Slot end time
   * @param {boolean} [data.isRecurring=false] - Boolean indicating if slot repeats
   * @param {string} [data.recurringPattern] - Pattern for recurring slots
   */
  constructor(data) {
    if (!data || typeof data !== 'object') {
      throw new ValidationError('TimeSlot data is required');
    }

    // Validate the time slot data
    validateTimeSlot(data);

    this._id = data._id || uuidv4();
    this.start = new Date(data.start);
    this.end = new Date(data.end);
    this.isRecurring = Boolean(data.isRecurring);
    this.recurringPattern = data.recurringPattern || null;

    // Validate recurring pattern if provided
    if (this.isRecurring && this.recurringPattern) {
      const validPatterns = ['daily', 'weekly', 'monthly', 'yearly'];
      if (!validPatterns.includes(this.recurringPattern)) {
        throw new ValidationError(
          'Invalid recurring pattern. Must be one of: ' + validPatterns.join(', '),
          'recurringPattern',
          this.recurringPattern
        );
      }
    }
  }

  /**
   * Check if a given time falls within this slot
   * @param {Date} time - Time to check
   * @returns {boolean} True if time falls within slot
   */
  isWithinSlot(time) {
    if (!time || !(time instanceof Date)) {
      return false;
    }
    
    return time >= this.start && time <= this.end;
  }

  /**
   * Check if this slot overlaps with another time period
   * @param {Date} start - Start time to check
   * @param {Date} end - End time to check
   * @returns {boolean} True if there's an overlap
   */
  overlaps(start, end) {
    if (!start || !end || !(start instanceof Date) || !(end instanceof Date)) {
      return false;
    }

    return this.start < end && this.end > start;
  }

  /**
   * Get the duration of this time slot in minutes
   * @returns {number} Duration in minutes
   */
  getDuration() {
    return Math.round((this.end - this.start) / (1000 * 60));
  }

  /**
   * Generate recurring instances of this time slot
   * @param {Date} fromDate - Start date for generation
   * @param {Date} toDate - End date for generation
   * @returns {Array<TimeSlot>} Array of time slot instances
   */
  generateRecurringInstances(fromDate, toDate) {
    if (!this.isRecurring || !this.recurringPattern) {
      return [this];
    }

    if (!fromDate || !toDate || !(fromDate instanceof Date) || !(toDate instanceof Date)) {
      throw new ValidationError('Valid from and to dates are required');
    }

    const instances = [];
    const duration = this.getDuration();
    let currentDate = new Date(fromDate);

    while (currentDate <= toDate) {
      // Create slot for current date
      const slotStart = new Date(currentDate);
      slotStart.setHours(this.start.getHours(), this.start.getMinutes(), 0, 0);
      
      const slotEnd = new Date(slotStart);
      slotEnd.setMinutes(slotEnd.getMinutes() + duration);

      if (slotStart >= fromDate && slotStart <= toDate) {
        instances.push(new TimeSlot({
          start: slotStart,
          end: slotEnd,
          isRecurring: false // Individual instances are not recurring
        }));
      }

      // Move to next occurrence
      switch (this.recurringPattern) {
        case 'daily':
          currentDate.setDate(currentDate.getDate() + 1);
          break;
        case 'weekly':
          currentDate.setDate(currentDate.getDate() + 7);
          break;
        case 'monthly':
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
        case 'yearly':
          currentDate.setFullYear(currentDate.getFullYear() + 1);
          break;
        default:
          // Unknown pattern, break to avoid infinite loop
          break;
      }
    }

    return instances;
  }

  /**
   * Convert to JSON string for persistence
   * @returns {string} JSON string representation
   */
  toJSON() {
    const data = {
      _id: this._id,
      start: this.start.toISOString(),
      end: this.end.toISOString(),
      isRecurring: this.isRecurring,
      recurringPattern: this.recurringPattern
    };
    return JSON.stringify(data);
  }

  /**
   * Create TimeSlot instance from JSON string
   * @param {string} jsonString - JSON string data
   * @returns {TimeSlot} New TimeSlot instance
   */
  static fromJSON(jsonString) {
    if (!jsonString || typeof jsonString !== 'string') {
      throw new ValidationError('Valid JSON string is required');
    }

    let jsonData;
    try {
      jsonData = JSON.parse(jsonString);
    } catch (error) {
      throw new ValidationError('Invalid JSON string format');
    }

    return new TimeSlot({
      _id: jsonData._id,
      start: new Date(jsonData.start),
      end: new Date(jsonData.end),
      isRecurring: jsonData.isRecurring,
      recurringPattern: jsonData.recurringPattern
    });
  }
} 